import { GoogleMap, InfoWindow, LoadScript } from '@react-google-maps/api';
import React, { useCallback, useEffect, useState } from 'react';
import { CareHome } from '../types';
import { trackCareHomeSelection } from '../utils/googleAnalytics';
import { createPinElement } from '../utils/mapUtils';
import CustomInfoWindow from './InfoWindow';

interface MapComponentProps {
    careHomes: CareHome[];
    center: { lat: number; lng: number };
    zoom?: number;
    onMapLoad?: (map: google.maps.Map) => void;
    selectedCareHome?: CareHome | null;
    closeInfoWindow?: boolean | number;
    /** When set, auto-opens the info window for this care home (update 2026-03-01.2) */
    initialInfoWindowCareHome?: CareHome | null;
    userId?: string;
    startingPostCode?: string;
    eventFilterActive?: boolean;
    /**
     * When this object reference changes, the map re-fits to include the
     * search centre plus all supplied points. Set to null to skip. (update 2026-04-22.1)
     */
    fitBoundsRequest?: {
        center: { lat: number; lng: number };
        points: { lat: number; lng: number }[];
    } | null;
}

const containerStyle = {
    width: '100%',
    height: '100%'
};

// Static libraries array to prevent LoadScript reloading on every render
const GOOGLE_MAPS_LIBRARIES: ("marker")[] = ['marker'];

const MapComponent: React.FC<MapComponentProps> = ({ 
    careHomes, 
    center, 
    zoom = 12, 
    onMapLoad,
    selectedCareHome,
    closeInfoWindow,
    initialInfoWindowCareHome,
    userId = '',
    startingPostCode = '',
    eventFilterActive = false,
    fitBoundsRequest = null
}) => {
    // Debug prop changes
    useEffect(() => {
        console.log('🗺️ MapComponent: selectedCareHome prop changed to:', selectedCareHome ? selectedCareHome.organisation_name : 'null');
    }, [selectedCareHome]);
    const [infoWindowCareHome, setInfoWindowCareHome] = useState<CareHome | null>(null);
    
    // Close InfoWindow when requested from parent
    useEffect(() => {
        if (closeInfoWindow) {
            console.log('🗺️ MapComponent: Closing InfoWindow on search result selection');
            setInfoWindowCareHome(null);
        }
    }, [closeInfoWindow]);

    const initialInfoWindowShown = React.useRef(false);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [markers, setMarkers] = useState<google.maps.marker.AdvancedMarkerElement[]>([]);

    const onLoad = useCallback((map: google.maps.Map) => {
        setMap(map);
        
        // Add click listener to clear selection when clicking on empty map area
        map.addListener('click', () => {
            // This will be handled by parent component if needed
            // For now, we'll just close the info window
            setInfoWindowCareHome(null);
        });
        
        onMapLoad?.(map);
    }, [onMapLoad]);

    const onUnmount = useCallback(() => {
        // Clean up markers when component unmounts
        markers.forEach(marker => {
            marker.map = null;
        });
        setMarkers([]);
        setMap(null);
    }, [markers]);

    // Create advanced markers when map or care homes change
    useEffect(() => {
        console.log('🗺️ MapComponent: useEffect triggered');
        console.log('🗺️ MapComponent: selectedCareHome prop:', selectedCareHome ? selectedCareHome.organisation_name : 'null');
        
        if (!map || !window.google?.maps?.marker?.AdvancedMarkerElement) {
            console.log('🗺️ MapComponent: Map or AdvancedMarkerElement not ready');
            return;
        }

        // Clear existing markers
        markers.forEach(marker => {
            marker.map = null;
        });

        // Sort care homes: reverse order (highest ranked first in list rendered last on map)
        // but keep selected one at the very end (on top of all others)
        const sortedCareHomes = [...careHomes].sort((a, b) => {
            const aSelected = selectedCareHome !== null && selectedCareHome !== undefined && selectedCareHome.id === a.id;
            const bSelected = selectedCareHome !== null && selectedCareHome !== undefined && selectedCareHome.id === b.id;
            
            // Selected care home always goes to the end (rendered last, appears on top)
            if (aSelected && !bSelected) return 1;
            if (!aSelected && bSelected) return -1;
            
            // For non-selected items, reverse the original order
            // This makes the first search result (highest ranked) render last among non-selected
            if (!aSelected && !bSelected) {
                const aIndex = careHomes.indexOf(a);
                const bIndex = careHomes.indexOf(b);
                return bIndex - aIndex; // Reverse order
            }
            
            return 0;
        });
        
        console.log('🗺️ MapComponent: Creating', sortedCareHomes.length, 'markers in reverse order (highest ranked on top)');
        if (selectedCareHome) {
            console.log('🗺️ MapComponent: Selected care home ID:', selectedCareHome.id, 'Name:', selectedCareHome.organisation_name);
        }

        // Create new markers using PinElement
        const newMarkers = sortedCareHomes.map((careHome, index) => {
            const isSelected = selectedCareHome !== null && selectedCareHome !== undefined && selectedCareHome.id === careHome.id;
            
            // Enhanced debug logging for each marker
            console.log(`📍 MapComponent: Creating marker ${index + 1}/${sortedCareHomes.length}:`, {
                name: careHome.organisation_name,
                id: careHome.id,
                idType: typeof careHome.id,
                isSelected: isSelected,
                selectedCareHome: selectedCareHome ? {
                    id: selectedCareHome.id,
                    idType: typeof selectedCareHome.id,
                    name: selectedCareHome.organisation_name
                } : null,
                comparison: selectedCareHome ? `${careHome.id} === ${selectedCareHome.id} = ${careHome.id === selectedCareHome.id}` : 'no selection'
            });
            
            if (isSelected) {
                console.log(`✨ MapComponent: Creating SELECTED pin for: ${careHome.organisation_name} (ID: ${careHome.id})`);
            }
            
            const pinElement = createPinElement(
                careHome.freshness_days, 
                careHome.open_score, 
                careHome.is_premium,
                eventFilterActive
            );
            
            // Apply selection styling to the pin element
            if (pinElement instanceof HTMLElement) {
                const className = isSelected ? 'selected-pin map-pin' : 'regular-pin map-pin';
                pinElement.className = className;
                
                console.log(`🎨 MapComponent: Styling pin for ${careHome.organisation_name}:`, {
                    isSelected,
                    className,
                    elementType: pinElement.tagName,
                    currentTransform: pinElement.style.transform,
                    computedStyle: window.getComputedStyle(pinElement).transform
                });
                
                // Inline styles as backup and for immediate effect
                if (isSelected) {
                    pinElement.style.transform = 'scale(1.8)';
                    pinElement.style.filter = 'drop-shadow(0px 4px 12px rgba(0,0,0,0.5))';
                    pinElement.style.zIndex = '1000';
                    // Add important to ensure it overrides other styles
                    pinElement.style.setProperty('transform', 'scale(1.8)', 'important');
                    pinElement.style.setProperty('filter', 'drop-shadow(0px 4px 12px rgba(0,0,0,0.5))', 'important');
                    
                    console.log(`✨ MapComponent: Applied SELECTED styling to ${careHome.organisation_name}:`, {
                        transform: pinElement.style.transform,
                        filter: pinElement.style.filter,
                        zIndex: pinElement.style.zIndex,
                        className: pinElement.className,
                        computedTransform: window.getComputedStyle(pinElement).transform
                    });
                } else {
                    pinElement.style.transform = 'scale(1.0)';
                    pinElement.style.filter = 'drop-shadow(0px 2px 4px rgba(0,0,0,0.3))';
                    pinElement.style.zIndex = '100';
                    pinElement.style.setProperty('transform', 'scale(1.0)', 'important');
                    pinElement.style.setProperty('filter', 'drop-shadow(0px 2px 4px rgba(0,0,0,0.3))', 'important');
                }
                pinElement.style.position = 'relative';
                pinElement.style.transition = 'all 0.3s ease';
                
                // Force a reflow to ensure styles are applied
                pinElement.offsetHeight;
            }
            
            const marker = new google.maps.marker.AdvancedMarkerElement({
                position: {
                    lat: careHome.latitude,
                    lng: careHome.longitude
                },
                map: map,
                content: pinElement,
                title: careHome.organisation_name,
                zIndex: isSelected ? 1000 : 100
            });

            // Add click listener for info window
            marker.addListener('click', () => {
                setInfoWindowCareHome(careHome);
                // Track map pin click in Google Analytics
                trackCareHomeSelection(careHome.organisation_name, 'map_pin');
            });

            return marker;
        });

        setMarkers(newMarkers);

        // Cleanup function
        return () => {
            newMarkers.forEach(marker => {
                marker.map = null;
            });
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, careHomes, selectedCareHome, eventFilterActive]);

    // Fit map bounds to include search centre and all results after a fresh search (update 2026-04-22.1)
    useEffect(() => {
        if (!map || !fitBoundsRequest || !window.google?.maps?.LatLngBounds) return;

        const { center: searchCenter, points } = fitBoundsRequest;
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(searchCenter);
        points.forEach(p => bounds.extend(p));

        // Asymmetric padding so results are not hidden behind the left sidebar on
        // desktop, and clear of the mobile results strip on small screens.
        const isDesktop = window.innerWidth > 768;
        const padding = isDesktop
            ? { top: 60, right: 40, bottom: 40, left: 390 }
            : { top: 60, right: 20, bottom: 160, left: 20 };

        map.fitBounds(bounds, padding);

        // For single-point / very-close results fitBounds will zoom in too far.
        // Cap the resulting zoom once the map settles.
        const MAX_FIT_ZOOM = 15;
        const listener = google.maps.event.addListenerOnce(map, 'idle', () => {
            const z = map.getZoom();
            if (typeof z === 'number' && z > MAX_FIT_ZOOM) {
                map.setZoom(MAX_FIT_ZOOM);
            }
        });

        return () => {
            google.maps.event.removeListener(listener);
        };
    }, [map, fitBoundsRequest]);

    // Open info window for deep-linked care home after map and markers are settled (update 2026-03-01.2)
    useEffect(() => {
        if (!initialInfoWindowCareHome || !map || markers.length === 0 || initialInfoWindowShown.current) return;
        const timer = setTimeout(() => {
            initialInfoWindowShown.current = true;
            setInfoWindowCareHome(initialInfoWindowCareHome);
        }, 150);
        return () => clearTimeout(timer);
    }, [initialInfoWindowCareHome, map, markers]);

    const handleInfoWindowClose = () => {
        setInfoWindowCareHome(null);
    };

    return (
        <LoadScript 
            googleMapsApiKey="AIzaSyBI61DaE-XQ83wP1ORYfDTdANm56mdpL8M"
            libraries={GOOGLE_MAPS_LIBRARIES}
        >
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={zoom}
                onLoad={onLoad}
                onUnmount={onUnmount}
                options={{
                    mapId: 'e67bf371a2993180f2d50a5f',
                    disableDefaultUI: false,
                    zoomControl: true,
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: true
                }}
            >
                {infoWindowCareHome && (() => {
                    const headerDiv = document.createElement('div');
                    headerDiv.className = 'info-org-name';
                    const nameSpan = document.createElement('span');
                    nameSpan.textContent = infoWindowCareHome.organisation_name;
                    headerDiv.appendChild(nameSpan);

                    return (
                        <InfoWindow
                            position={{
                                lat: infoWindowCareHome.latitude,
                                lng: infoWindowCareHome.longitude
                            }}
                            onCloseClick={handleInfoWindowClose}
                            options={{
                                headerContent: headerDiv
                            }}
                        >
                            <CustomInfoWindow 
                                careHome={infoWindowCareHome} 
                                userId={userId}
                                startingPostCode={startingPostCode}
                                searchPosition={careHomes.findIndex(ch => ch.id === infoWindowCareHome.id) + 1}
                                eventFilterActive={eventFilterActive}
                            />
                        </InfoWindow>
                    );
                })()}
            </GoogleMap>
        </LoadScript>
    );
};

export default MapComponent; 